import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { AssignmentTurnedInOutlined, HourglassTopOutlined, PowerSettingsNewOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { medicalCheckupsService } from '../../services/medicalCheckupsService';
import { medicsService } from '../../services/medicsService';
import { MedicalCheckupQueueOfferDto } from '../../models/medicalCheckups/medicalCheckupQueueOfferDto';
import { ROUTES } from '../../constants/routes';
import { notificationSoundService } from '../../services/notificationSoundService';

const POLL_INTERVAL_MS = 3000;

const getError = (error: any) => error?.response?.data?.message || error?.message || 'Не удалось выполнить действие';

export const MedicalCheckupQueueBoard = () => {
  const navigate = useNavigate();
  const lastSoundOfferIdRef = useRef<number | null>(null);
  const [offer, setOffer] = useState<MedicalCheckupQueueOfferDto | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  const progress = useMemo(() => {
    if (!offer) return 0;
    const total = Math.max(1, Math.round((new Date(offer.expiresAt).getTime() - new Date(offer.offeredAt).getTime()) / 1000));
    return Math.max(0, Math.min(100, (secondsLeft / total) * 100));
  }, [offer, secondsLeft]);

  const loadOffer = useCallback(async () => {
    try {
      const response = await medicalCheckupsService.getCurrentQueueOffer();
      const nextOffer = response.data.data;

      // Accepted assignments must survive a page refresh and remain accessible
      // even when the medic has switched off receiving new queue offers.
      if (nextOffer?.status === 'Accepted' || nextOffer?.status === 2) {
        navigate(ROUTES.medicalCheckups.idPath.replace(':id', String(nextOffer.medicalCheckupId)));
        return;
      }

      if (!available) {
        setOffer(null);
        setSecondsLeft(0);
        lastSoundOfferIdRef.current = null;
        return;
      }

      if (nextOffer?.status === 'Offered' || nextOffer?.status === 1) {
        if (nextOffer.offerId !== lastSoundOfferIdRef.current) {
          lastSoundOfferIdRef.current = nextOffer.offerId;
          void notificationSoundService.play();
        }
      } else if (!nextOffer) {
        lastSoundOfferIdRef.current = null;
      }

      setOffer(nextOffer);
      setSecondsLeft(nextOffer?.secondsLeft ?? 0);
      setError('');
    } catch (e) {
      setError(getError(e));
    } finally {
      setLoading(false);
    }
  }, [available, navigate]);

  useEffect(() => {
    medicsService.getQueueAvailability()
      .then((response) => setAvailable(response.data.data))
      .catch((e) => setError(getError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadOffer();
    const interval = setInterval(loadOffer, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadOffer]);

  useEffect(() => {
    if (!offer) return;
    const interval = setInterval(() => {
      setSecondsLeft((value) => Math.max(0, value - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [offer]);

  const toggleAvailability = async (value: boolean) => {
    setError('');
    setLoading(true);
    try {
      const response = await medicsService.setQueueAvailability(value);
      setAvailable(response.data.data);
      if (!response.data.data) {
        setOffer(null);
        setSecondsLeft(0);
        lastSoundOfferIdRef.current = null;
      }
    } catch (e) {
      setError(getError(e));
    } finally {
      setLoading(false);
    }
  };

  const accept = async () => {
    if (!offer) return;
    setAccepting(true);
    setError('');
    try {
      const response = await medicalCheckupsService.acceptQueueOffer(offer.medicalCheckupId);
      if (response.data.data > 0) {
        navigate(ROUTES.medicalCheckups.idPath.replace(':id', String(offer.medicalCheckupId)));
        return;
      }
      setError('Заявка уже недоступна. Система выдаст следующую автоматически.');
      await loadOffer();
    } catch (e) {
      setError(getError(e));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 980 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={800}>Очередь входящих осмотров</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Заявка появляется только у одного доступного медика.
              </Typography>
            </Box>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PowerSettingsNewOutlined color={available ? 'success' : 'disabled'} />
              <Typography fontWeight={700}>{available ? 'На линии' : 'Недоступен'}</Typography>
              <Switch checked={available} onChange={(_, value) => toggleAvailability(value)} disabled={loading || accepting} />
            </Stack>
          </Stack>

          <Divider />

          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Box py={8} textAlign="center"><CircularProgress /></Box>
          ) : !available ? (
            <Box py={8} textAlign="center">
              <PowerSettingsNewOutlined sx={{ fontSize: 54, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" fontWeight={800}>Прием заявок выключен</Typography>
              <Typography color="text.secondary">Включите статус “На линии”, чтобы получать осмотры из очереди.</Typography>
            </Box>
          ) : offer ? (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fbfbff', borderRadius: 2 }}>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="overline" color="primary" fontWeight={800}>Входящая заявка</Typography>
                    <Typography variant="h4" fontWeight={900}>Новый осмотр</Typography>
                    <Typography color="text.secondary">Попытка назначения: {offer.attemptNumber}</Typography>
                  </Box>
                  <Box textAlign={{ xs: 'left', sm: 'right' }}>
                    <Typography variant="h3" fontWeight={900} color={secondsLeft <= 5 ? 'error.main' : 'primary.main'}>
                      {secondsLeft}
                    </Typography>
                    <Typography color="text.secondary">секунд</Typography>
                  </Box>
                </Stack>

                <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button
                    size="large"
                    variant="contained"
                    startIcon={accepting ? <CircularProgress color="inherit" size={18} /> : <AssignmentTurnedInOutlined />}
                    disabled={accepting || secondsLeft <= 0}
                    onClick={accept}
                    sx={{ minHeight: 48, px: 4 }}
                  >
                    Взять в работу
                  </Button>
                  <Typography color="text.secondary">
                    Если время истечет, заявка автоматически уйдет следующему свободному медику.
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <Box py={8} textAlign="center">
              <HourglassTopOutlined sx={{ fontSize: 54, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" fontWeight={800}>Ожидание заявки</Typography>
              <Typography color="text.secondary">Вы на линии. Система покажет следующий осмотр автоматически.</Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
